import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import {
  AssignmentCustomerResponse,
  AssignmentPspResponse,
  baseAssignmentsActions,
  baseAssignmentsSelectors,
  baseAuthSelectors,
  BaseButtonSize,
  BaseCoreState,
  BaseFormatDatePipe,
  BaseModalService,
  BaseSignatureService,
  BaseSigningStatusBySide,
  BaseTokenInterface,
  ContractResponse,
} from 'base';

@Component({
  selector: 'base-customer-employee-operation-info-tab',
  templateUrl: './base-customer-employee-operation-info-tab.component.html',
})
export class BaseCustomerEmployeeOperationInfoTabComponent implements OnInit, OnDestroy {
  @Output() signContract: EventEmitter<any> = new EventEmitter<any>();
  @Output() deregisterEmployee: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('documentViewerTemplate') documentViewerTemplate: ElementRef;
  buttonSize = BaseButtonSize;
  signatureStatus = BaseSigningStatusBySide;
  assignment: AssignmentCustomerResponse;
  documentViewerModalRef: NgbModalRef | undefined;
  contractFile: { data: Blob | undefined; fileName: string | undefined };
  loggedInUserId: number;
  ownSignatureStatus: string;
  partnerSignatureStatus: string;
  ownSignatureStatusParams: any;
  partnerSignatureStatusParams: any;
  validContract: ContractResponse;

  private destroy$ = new Subject();

  constructor(
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    private translateService: TranslateService,
    private formatDatePipe: BaseFormatDatePipe,
    private router: Router,
    private baseSignatureService: BaseSignatureService
  ) {}

  ngOnInit(): void {
    this.store
      .select(baseAssignmentsSelectors.getDocumentView)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.data)
      )
      .subscribe((document) => {
        this.contractFile = document;

        if (!this.documentViewerModalRef) {
          this.openDocumentViewer();
        }
      });

    combineLatest([
      this.store.select(baseAssignmentsSelectors.getAssignment),
      this.store.select(baseAuthSelectors.getUserInfo),
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(([assignmentState, userState]) => !!assignmentState.ok && !!userState.ok),
        map(
          ([assignmentState, userState]) =>
            [assignmentState.data, userState.data] as [
              AssignmentPspResponse | AssignmentCustomerResponse,
              BaseTokenInterface
            ]
        )
      )
      .subscribe(([assignment, user]) => {
        this.loggedInUserId = user.id;
        this.assignment = assignment as AssignmentCustomerResponse;
        this.validContract = this.assignment.contracts?.find((contract) => contract.valid) as ContractResponse;
        this.setAssignmentSignatureStatus();
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  viewContract(): void {
    this.store.dispatch(
      baseAssignmentsActions.viewContract({
        payload: { assignment: this.assignment.id, contract: this.validContract.id },
      })
    );
  }

  onSignContractClick(event: Event): void {
    this.signContract.emit(event);
  }

  onDeregisterEmployeeClick(): void {
    this.deregisterEmployee.emit(true);
  }

  reloadAssignment(assignment: number): void {
    this.store.dispatch(baseAssignmentsActions.reloadAssignmentData({ payload: { assignment } }));
  }

  openIzsInfoModal(event: Event): void {
    event.stopPropagation();
    this.modalService.closeAllModals();
    this.router.navigate(['/service-provider'], {
      queryParams: {
        page: 1,
        search: this.assignment.pspBranch.name,
        pspBranchId: this.assignment.pspBranch.id,
        location: this.assignment.customerLocation.id,
      },
    });
  }

  private openDocumentViewer(): void {
    if (this.baseSignatureService.baseSignContractsInfoCardIsInitSubject.value) {
      return;
    }

    this.documentViewerModalRef = this.modalService.open(
      {
        bodyTemplate: this.documentViewerTemplate,
        cancelButtonDisplayed: false,
        okButtonDisplayed: false,
        hideHeader: true,
        hideFooter: true,
      },
      { size: 'lg' }
    );
    this.documentViewerModalRef.result.then(
      () => this.cleanDocumentViewer(),
      () => this.cleanDocumentViewer()
    );
  }

  private setAssignmentSignatureStatus(): void {
    this.ownSignatureStatus =
      this.validContract?.signatures?.statusCustomer === this.signatureStatus.SIGNED
        ? 'externalEmployees.contractStatus.signedByOwnSide'
        : this.validContract?.signatures?.statusCustomer === this.signatureStatus.IN_QUEUE
        ? 'externalEmployees.contractStatus.ownSideInQueue'
        : this.validContract?.signatures?.statusCustomer === this.signatureStatus.ERROR
        ? 'externalEmployees.contractStatus.ownSideError'
        : 'externalEmployees.contractStatus.notSignedByOwnSide';

    this.ownSignatureStatusParams =
      this.validContract?.signatures?.statusCustomer === this.signatureStatus.UNSIGNED
        ? {}
        : {
            signedAt: this.formatDatePipe.transform(this.validContract?.signedAtCustomer),
            signingMethod: this.translateService.instant(
              `externalEmployees.contractSignMethod.${this.validContract?.type}`
            ),
            signedBy:
              this.validContract?.signedByCustomerUser?.id === this.loggedInUserId
                ? this.translateService.instant('label.you')
                : `${this.validContract?.signedByCustomerUser?.firstName} ${this.validContract?.signedByCustomerUser?.lastName}`,
          };

    this.partnerSignatureStatus =
      this.validContract?.signatures?.statusPsp === this.signatureStatus.SIGNED
        ? 'externalEmployees.contractStatus.signedByPartner'
        : 'externalEmployees.contractStatus.notSignedByPartner';

    this.partnerSignatureStatusParams =
      this.validContract?.signatures?.statusPsp === this.signatureStatus.SIGNED
        ? {
            signedAt: this.formatDatePipe.transform(this.validContract?.signedAtPSP),
            signingMethod: this.translateService.instant(
              `externalEmployees.contractSignMethod.${this.validContract.type}`
            ),
            signedBy: `${this.validContract?.signedByPspUser?.firstName} ${this.validContract?.signedByPspUser?.lastName}`,
          }
        : {};
  }

  private cleanDocumentViewer(): void {
    this.store.dispatch(baseAssignmentsActions.clearDocumentView());
    this.documentViewerModalRef = undefined;
  }
}
